import React from 'react';
import { fmtMoney, fmtDelta, approxEq } from '../../utils/priceMath';
import { basisLabel } from '../../utils/quotePricing';

// "Recalculate All Prices" preview (F01 #3). Nothing on the quote changes
// until Apply: the modal lists every line with its basis, old → new price and
// delta, plus old total → new total, so an operator can see a 75%-target
// line stay at 75% and a manual line keep its price while its breakdown
// refreshes.

const KIND_LABEL = { manufactured: 'part', purchased: 'component', ops: 'ops' };

export default function QuoteRecalcPreviewModal({ show, rows, oldTotal, newTotal, failures, overrideNote, onApply, onCancel }) {
  if (!show) return null;
  const changed = (rows || []).filter(r => r.changed).length;
  const refreshed = (rows || []).filter(r => r.breakdownRefreshed).length;
  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Recalculate All Prices — preview</h5>
            <button type="button" className="btn-close" onClick={onCancel} aria-label="Close"></button>
          </div>
          <div className="modal-body" style={{ fontSize: '0.85rem' }}>
            <p className="mb-2">
              Fresh breakdowns were fetched for {refreshed} line{refreshed === 1 ? '' : 's'}. Each line's price is re-derived from
              <strong> its own basis</strong>: calculated lines follow the new calculated price, target-margin lines keep their margin on the
              new cost, cost+markup components keep their markup, and manual / book lines keep their price (breakdown only).
              {' '}{changed} price{changed === 1 ? '' : 's'} would change. Nothing is saved until you Apply and then Save Changes.
            </p>
            {failures && failures.length > 0 && (
              <div className="alert alert-warning py-1 px-2 mb-2">
                Could not recalculate: {failures.join('; ')} — those lines keep their current breakdown and price.
              </div>
            )}
            {overrideNote && <div className="alert alert-info py-1 px-2 mb-2">{overrideNote}</div>}
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-2 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Line</th>
                    <th>Type</th>
                    <th>Basis</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Old price</th>
                    <th className="text-end">New price</th>
                    <th className="text-end">Δ / unit</th>
                    <th className="text-end">Old ext → New ext</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(rows || []).map(r => (
                    <tr key={r.id} className={r.changed ? '' : 'text-muted'}>
                      <td>{r.label}</td>
                      <td>{KIND_LABEL[r.kind] || r.kind}</td>
                      <td>{basisLabel({ price_source: r.basis, price_basis: r.basisValue })}</td>
                      <td className="text-end">{r.qty}</td>
                      <td className="text-end">{fmtMoney(r.oldPrice)}</td>
                      <td className={`text-end ${r.changed ? 'fw-bold' : ''}`}>{fmtMoney(r.newPrice)}</td>
                      <td className={`text-end ${r.changed ? (r.delta > 0 ? 'text-success' : 'text-danger') : ''}`}>{fmtDelta(r.delta)}</td>
                      <td className="text-end">{fmtMoney(r.oldExt)} → {fmtMoney(r.newExt)}</td>
                      <td className="small text-muted">
                        {r.breakdownRefreshed ? 'breakdown refreshed' : 'breakdown unchanged'}
                        {r.note ? ` · ${r.note}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan={7} className="text-end">Subtotal (overrides honoured)</td>
                    <td className="text-end">{fmtMoney(oldTotal)} → {fmtMoney(newTotal)} <span className={approxEq(oldTotal, newTotal) ? 'text-muted' : (newTotal > oldTotal ? 'text-success' : 'text-danger')}>({fmtDelta(newTotal - oldTotal)})</span></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel — keep everything as is</button>
            <button type="button" className="btn btn-primary" onClick={onApply}>Apply to quote</button>
          </div>
        </div>
      </div>
    </div>
  );
}
